import { authApi, dataCentralPublic, foundationApiPublic } from '@/api/client';
import {
  getAttribution,
  getAffiliateRefCode,
  getAffiliateVisitorId,
} from '@/lib/attribution';
import { brand } from '@/config/brand';

/**
 * Auth-flow API — login, MFA, email verification, forgot/reset password.
 * Mirrors the legacy @console contracts. Every endpoint returns a
 * `BaseResponse<T>` envelope: `{ data?, errors? }`.
 */

export interface ApiError {
  code: number;
  message: string;
}

export interface BaseResponse<T> {
  data?: T;
  errors?: ApiError[];
}

/** Verification-incomplete error code returned by /auth/login. */
export const ERR_VERIFICATION_REQUIRED = 1401;

export interface SessionTokens {
  access_token?: string;
  refresh_token?: string;
  /** Absolute expiry as a UNIX timestamp in seconds. */
  exp?: number;
  uuid?: string;
}

export interface LoginData extends SessionTokens {
  mfa?: {
    enable: boolean;
    method: 'totp' | 'email';
  };
}

export interface VerificationData {
  created?: boolean;
}

// --- Login ------------------------------------------------------------------

export async function loginUser(values: {
  email: string;
  password: string;
}): Promise<BaseResponse<LoginData>> {
  const res = await authApi.post('/auth/login', values);
  return res.data;
}

// --- Email verification (account activation / email MFA) --------------------

/** Request an email verification code be sent to `email`. */
export async function resolveVerification(
  email: string,
): Promise<BaseResponse<VerificationData>> {
  const res = await authApi.post('/auth/v2/verification', { email });
  return res.data;
}

/** Validate an emailed verification code; returns a session on success. */
export async function verificationValidation(payload: {
  email: string;
  verification: string;
}): Promise<BaseResponse<SessionTokens>> {
  const res = await authApi.post('/auth/v2/verification/validate', payload);
  return res.data;
}

// --- TOTP / backup-code MFA at login ----------------------------------------

export async function verifyMfaLogin(payload: {
  email: string;
  password: string;
  otp_code?: string;
  backup_code?: string;
}): Promise<BaseResponse<SessionTokens>> {
  const res = await authApi.post('/auth/v2/mfa/login/verify', payload);
  return res.data;
}

// --- Registration (SSO) -----------------------------------------------------

export interface RegisterPayload {
  type: 'individual' | 'business';
  email: string;
  password: string;
  password_confirmation: string;
  // Business-only fields. NOTE: confirm exact field names/mapping with the
  // auth team — the template labels (Contact Person, Email Business, Country…)
  // are mapped here on a best-effort basis.
  company_name?: string;
  phone?: string;
  country?: string;
  contact_person?: string;
  // First-touch marketing attribution (utm_* captured at the landing page). Set
  // automatically by registerUser from the session attribution — callers don't
  // pass these. The backend maps them to the customer's structured source channel.
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  // Affiliate referral code, read from the `_gcl_aff` cookie (DataCentral's /ref/:code handler).
  // Set automatically by registerUser — callers don't pass it. The backend resolves it to an
  // affiliate and links the new customer (customers.referrer_id).
  ref_code?: string;
  // Affiliate click visitor id (from the `visitor_id` cookie). Lets the backend mark the exact
  // click converted (populates the affiliate's registrations tab). Set automatically too.
  visitor_id?: string;
  // Signup email-verification code (from requestRegisterOtp). Required by the
  // backend before it creates the account — blocks fake/unowned emails.
  otp?: string;
  // Reseller org customer_id — set automatically by registerUser from the active brand
  // (brand.resellerCustomerId) on reseller-branded builds. The backend links the new customer
  // under this reseller (reseller_customers). Undefined for first-party brands → backend skips it.
  reseller_customer_id?: number;
}

export interface RegisterData extends SessionTokens {
  email?: string;
  auth_method?: 'sso' | 'local';
}

/**
 * Request a signup email-verification OTP for `email`. Sends a 6-digit code the
 * user must submit via `registerUser({ otp })`. The backend rejects an
 * already-registered email (409) — the caller surfaces that as "please log in".
 */
export async function requestRegisterOtp(email: string): Promise<void> {
  await dataCentralPublic.post('/partner/auth/register/otp', { email });
}

export async function registerUser(
  payload: RegisterPayload,
): Promise<BaseResponse<RegisterData>> {
  // Attach first-touch marketing attribution so the backend can set the customer's
  // source channel (gg_ads / fb_ads / marketing_approach); organic signups have none.
  const attr = getAttribution();
  const body: RegisterPayload = {
    ...payload,
    utm_source: payload.utm_source ?? attr?.utm_source,
    utm_medium: payload.utm_medium ?? attr?.utm_medium,
    utm_campaign: payload.utm_campaign ?? attr?.utm_campaign,
    // Forward the affiliate ref from the _gcl_aff cookie so the backend can attribute this
    // signup to the affiliate (cross-TLD: the cookie can't ride the *.vnetwork.dev API request).
    ref_code: payload.ref_code ?? getAffiliateRefCode(),
    visitor_id: payload.visitor_id ?? getAffiliateVisitorId(),
    // On a reseller-branded build, link the new customer under the reseller (ownership; separate
    // from the affiliate ref above). Undefined for first-party brands → omitted from the payload.
    reseller_customer_id: payload.reseller_customer_id ?? brand.resellerCustomerId,
  };
  const res = await dataCentralPublic.post('/partner/auth/register/sso', body);
  // DataCentral wraps the payload as `{ message, result }`, not the `{ data, errors }`
  // envelope the callers expect. Normalize so a token-bearing response drives the
  // auto-login path (setSession → dashboard) instead of silently falling to /login.
  return { data: res.data?.result };
}

// --- Google standalone sign-in (direct, NOT via Keycloak) -------------------

export interface GoogleLoginResult extends SessionTokens {
  email?: string;
  auth_method?: 'google';
}

/**
 * Exchange a Google OAuth2 authorization `code` (from the in-app "Continue with
 * Google" popup) for app tokens. The backend (`POST /partner/auth/google`)
 * exchanges the code with Google (client id + secret; popup exchanges use
 * redirect_uri `postmessage`), get-or-creates the matching Keycloak user +
 * customer contact, and returns Keycloak-issued tokens — so the session refreshes
 * against Keycloak (marked 'google', which dispatches to the Keycloak refresh).
 * `exp` is an absolute UNIX timestamp.
 */
export async function exchangeGoogleCode(payload: {
  code: string;
}): Promise<GoogleLoginResult> {
  // Attach first-touch marketing attribution so a Google signup gets the same
  // structured source channel as a form registration (organic signups have none).
  const attr = getAttribution();
  const body = {
    ...payload,
    utm_source: attr?.utm_source,
    utm_medium: attr?.utm_medium,
    utm_campaign: attr?.utm_campaign,
    // Forward the affiliate ref from the _gcl_aff cookie so a Google signup is attributed to
    // the affiliate too (cross-TLD: the cookie can't ride the *.vnetwork.dev API request).
    ref_code: getAffiliateRefCode(),
    visitor_id: getAffiliateVisitorId(),
    // Reseller ownership attribution for a whitelabel build (same as the form signup above).
    reseller_customer_id: brand.resellerCustomerId,
  };
  const res = await dataCentralPublic.post('/partner/auth/google', body);
  // DataCentral wraps token responses as `{ message, result }`, mirroring the
  // SSO callback and register endpoints.
  return res.data?.result;
}

// --- SSO (VNETWORK-ID) code exchange ----------------------------------------

export interface SsoCallbackResult extends SessionTokens {
  /** Present when the SSO account additionally requires MFA before tokens. */
  session_id?: string;
  mfa_required?: boolean;
  mfa_method?: 'totp' | 'email';
}

/** Exchange the authorization `code` for app tokens via the backend. */
export async function exchangeSsoCode(payload: {
  code: string;
  code_verifier: string;
  redirect_uri: string;
}): Promise<SsoCallbackResult> {
  const res = await dataCentralPublic.post('/partner/auth/sso/callback', payload);
  return res.data?.result;
}

/**
 * Complete SSO MFA: exchange the held `session_id` (returned by the callback for
 * MFA-protected accounts) plus the authenticator/backup code for the held
 * tokens. `otp_code` carries either the 6-digit TOTP or a backup code.
 */
export async function verifySsoMfa(payload: {
  session_id: string;
  otp_code: string;
}): Promise<SsoCallbackResult> {
  const res = await dataCentralPublic.post('/partner/auth/sso/mfa/verify', payload);
  return res.data?.result;
}

// --- Forgot / reset password (datacentral v4) -------------------------------

export async function forgotPassword(payload: {
  email: string;
}): Promise<BaseResponse<unknown>> {
  const res = await dataCentralPublic.post('/partner/auth/forgotpw', payload);
  return res.data;
}

export interface ValidateResetTokenResult {
  valid: boolean;
  email?: string;
}

export async function validateResetToken(
  token: string,
): Promise<ValidateResetTokenResult> {
  const res = await dataCentralPublic.get('/partner/auth/resetpw/validate', {
    params: { token },
  });
  return res.data?.result;
}

export async function resetPassword(payload: {
  token: string;
  new_password: string;
  new_password_confirmation: string;
}): Promise<BaseResponse<unknown>> {
  const res = await dataCentralPublic.post('/partner/auth/resetpw', payload);
  return res.data;
}

// --- SSO auto-migration (datacentral v4) ------------------------------------

export interface MigrationableResult {
  migrationable: boolean;
  is_customer: boolean;
  has_local: boolean;
  has_sso: boolean;
  /** Local account exists but its legacy login is locked (already migrated). */
  local_locked: boolean;
}

/**
 * Ask the backend whether an email must migrate from local/CRM to SSO. Public,
 * pre-login. An unknown or already-migrated email yields `migrationable:false`.
 */
export async function checkMigrationable(
  email: string,
): Promise<MigrationableResult> {
  const res = await dataCentralPublic.post('/partner/auth/migrationable', { email });
  return res.data?.result;
}

/** Trigger the migration OTP email. Always resolves generically (no enumeration). */
export async function sendMigrationOtp(email: string): Promise<void> {
  await dataCentralPublic.post('/partner/auth/migration/otp', { email });
}

/** Verify the migration OTP; returns a short-lived one-time migration token. */
export async function verifyMigrationOtp(payload: {
  email: string;
  otp: string;
}): Promise<{ migration_token: string }> {
  const res = await dataCentralPublic.post('/partner/auth/migration/verify', payload);
  return res.data?.result;
}

/** Finish migration: set the SSO password using the migration token. Optional
 * name/phone/customer_id are supplied by the reconciliation onboarding form. */
export async function setMigrationPassword(payload: {
  migration_token: string;
  new_password: string;
  new_password_confirmation: string;
  name?: string;
  phone?: string;
  customer_id?: number;
}): Promise<{ email: string }> {
  const res = await dataCentralPublic.post('/partner/auth/migration/setpassword', payload);
  return res.data?.result;
}

// --- Reconciliation deep-link (via @mgmt) -----------------------------------

export interface ReconciliationInvoice {
  invoice_id: number;
  invoice_code: string;
  total: number;
  currency: string;
  status: string;
  apply_start: string;
  apply_end: string;
}

export interface ReconciliationResolveResult {
  email: string;
  customer_id: number;
  has_local: boolean;
  has_sso: boolean;
  invoice: ReconciliationInvoice;
  prefill: { name: string; phone: string };
}

/**
 * Resolve a reconciliation deep-link token via margarita-foundation (which decrypts it and
 * validates against DataCentral). Public — the encrypted token is the credential.
 */
export async function resolveReconciliation(
  token: string,
): Promise<ReconciliationResolveResult> {
  const res = await foundationApiPublic.post('/reconciliation/resolve', { token });
  return res.data;
}
